/**
 * User display utilities
 * 
 * These utilities handle displaying user information throughout the app,
 * with display_name as the primary display method and fallback to fun farmer names.
 */

interface UserLike {
  email?: string;
  display_name?: string;
}

/**
 * Fun farmer names for fallback display
 */
const FARMER_NAMES = [
  'Bubbles', 'Ricky', 'Julian', 'Billy Bob', 'Cletus', 'Earl',
  'Daisy Mae', 'Bobby Sue', 'Jolene', 'Billiam', 'Cooter', 'Scooter',
  'Lurleen', 'Brandine', 'Clyde', 'Merle', 'Daryl', 'Jethro',
  'Lula Belle', 'Bertha', 'Angus', 'Roscoe', 'Tater', 'Skeeter',
  'Peggy Sue', 'Beauregard', 'Chester', 'Hank', 'Dolly', 'Gus',
];

/**
 * Get the best display name for a user.
 * Priority: display_name > "Farmer [FunnyName]"
 * 
 * @param user - User object with email and/or display_name
 * @returns A human-readable display name
 */
export function getDisplayName(user?: UserLike | null): string {
  if (!user) return 'Unknown';
  
  // Prefer display_name if available and not empty
  if (user.display_name && user.display_name.trim()) {
    return user.display_name;
  }
  
  // Fallback: Random farmer name (seeded by email for consistency)
  if (user.email) {
    return generateDisplayNameFromEmail(user.email);
  }
  
  return 'Farmer Unknown';
}

/**
 * Generate a "Farmer [Name]" display name.
 * Uses email hash for consistency (same email always gets same name).
 * 
 * @param email - Email address to seed the random selection
 * @returns A farmer display name
 */
export function generateDisplayNameFromEmail(email: string): string {
  if (!email || typeof email !== 'string') return 'Farmer Unknown';
  
  // Simple hash from email for consistent name assignment
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    const char = email.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  const index = Math.abs(hash) % FARMER_NAMES.length;
  return `Farmer ${FARMER_NAMES[index]}`;
}

/**
 * Get initials from a user's display name or email.
 * Used for avatar displays.
 * 
 * @param user - User object with email and/or display_name
 * @returns 2-character initials
 */
export function getInitials(user?: UserLike | null): string {
  const name = getDisplayName(user);
  const parts = name.split(' ').filter(Boolean);
  
  if (parts.length >= 2) {
    // First and last initial
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  
  // Single word: first two characters
  return name.substring(0, 2).toUpperCase();
}

/**
 * Get a short version of the display name (first name only).
 * Useful for compact displays like "Replying to John"
 * 
 * @param user - User object with email and/or display_name
 * @returns First name or short display name
 */
export function getShortDisplayName(user?: UserLike | null): string {
  const name = getDisplayName(user);
  const parts = name.split(' ').filter(Boolean);
  
  if (parts.length > 0) {
    return parts[0];
  }
  
  return name;
}

